import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import AppLauncher from './components/AppLauncher';
import { SetupWizard } from './components/SetupWizard';
import { setupWizardApi } from './utils/api/setupWizard';
import { GlobalProvider } from './contexts/GlobalContext';
import { useSetupWizard } from './contexts/GlobalContext';
import { getApiBaseUrl } from './utils/api';
import { AuthProvider } from './contexts/AuthContext';
import { SpacesProvider } from './contexts/SpacesContext';
import { ParentApiProvider } from './utils/parentApi';
import { ToastBridge } from './utils/parentApi/ToastBridge';

// Force skip wizard during development
const SKIP_WIZARD = process.env.REACT_APP_SKIP_WIZARD === 'true' || true;

function App() {
  const [showWizard, setShowWizard] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <GlobalProvider>
        <AuthProvider>
          <SpacesProvider>
            <AppInitializer
              showWizard={showWizard}
              setShowWizard={setShowWizard}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </SpacesProvider>
        </AuthProvider>
      </GlobalProvider>
    </GoogleOAuthProvider>
  );
}

const AppInitializer = ({ showWizard, setShowWizard, isLoading, setIsLoading }) => {
  const { updateSetupConfig, setConfigured } = useSetupWizard();

  useEffect(() => {
    const init = async () => {
      if (SKIP_WIZARD) {
        setShowWizard(false);
        setConfigured(true);
        setIsLoading(false);
        return;
      }

      const baseUrl = getApiBaseUrl();

      try {
        const isSetup = await setupWizardApi.isConfigured();
        if (isSetup) {
          // Already configured, load directly
          setShowWizard(false);
          setConfigured(true);
        } else {
          // Not configured, show wizard
          setShowWizard(true);
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
        // If we can't connect to the API, we still show the wizard
        setShowWizard(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Run only once on mount
    init();
  }, []); // No dependencies here

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // If SKIP_WIZARD is true, always render AppLauncher
  if (SKIP_WIZARD) {
    return (
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <ParentApiProvider>
          <ToastBridge />
          <Routes>
            <Route path="*" element={<AppLauncher />} />
          </Routes>
        </ParentApiProvider>
      </Router>
    );
  }

  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <ParentApiProvider>
        <ToastBridge />
        {showWizard ? (
          <SetupWizard
            onComplete={async () => {
              setShowWizard(false);
              // Once completed, fetch config and update global state
              const savedConfig = await setupWizardApi.getConfig();
              updateSetupConfig(savedConfig);
            }}
          />
        ) : (
          <Routes>
            <Route path="*" element={<AppLauncher />} />
          </Routes>
        )}
      </ParentApiProvider>
    </Router>
  );
};

export default App;